import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SetupAction, SetupPhase } from "@/hooks/agent/useGenieSetupReducer";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { MOCK_GENIE_DATA } from "@/types/shared/mock-data";

interface TestModeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadConfiguration: (phase: SetupPhase) => void;
  dispatch: React.Dispatch<SetupAction>;
}

export const TestModeModal = ({ open, onOpenChange, onLoadConfiguration, dispatch }: TestModeModalProps) => {
  const handleTestModeWithConfig = (testModeJson: string) => {
    try {
      if (!testModeJson.trim()) {
        dispatch({ type: "SET_ANALYSIS_RESULT", payload: MOCK_GENIE_DATA });
        toast.success("Loaded default test configuration");
      } else {
        const parsedConfig = JSON.parse(testModeJson) as SetupAnalysisResult;

        if (!parsedConfig.genieName || !parsedConfig.useCase || !parsedConfig.systemPrompt) {
          toast.error("Missing required fields: genieName, useCase, or systemPrompt");
          return;
        }

        if (
          !parsedConfig.conversationSettings?.maxDuration ||
          parsedConfig.conversationSettings?.turnTimeout === undefined ||
          parsedConfig.conversationSettings?.silenceTimeout === undefined
        ) {
          toast.error("Invalid conversation settings");
          return;
        }

        const configWithDefaults: SetupAnalysisResult = {
          ...MOCK_GENIE_DATA,
          ...parsedConfig,
          conversationSettings: {
            ...MOCK_GENIE_DATA.conversationSettings,
            ...parsedConfig.conversationSettings,
          },
        };

        dispatch({ type: "SET_ANALYSIS_RESULT", payload: configWithDefaults });
        toast.success("Loaded custom test configuration");
      }

      onOpenChange(false);
      onLoadConfiguration("confirmation");
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        toast.error("Failed to parse configuration");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Test Mode Configuration</DialogTitle>
          <DialogDescription>
            Paste a SetupAnalysisResult JSON object or leave empty to use default mock data
          </DialogDescription>
        </DialogHeader>
        <TestModeForm
          onSubmit={handleTestModeWithConfig}
          onCancel={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

interface TestModeFormProps {
  onSubmit: (json: string) => void;
  onCancel: () => void;
}

const TestModeForm = ({ onSubmit, onCancel }: TestModeFormProps) => {
  const [testModeJson, setTestModeJson] = useState("");

  return (
    <div className="space-y-4 py-4">
      <Textarea
        placeholder={`{
  "genieName": "Bird Nova",
  "useCase": "Identify bird species based on user-described details.",
  "language": "en",
  "voicePreference": "middle-aged female American voice; friendly and pleasant",
  "conversationSettings": {
    "maxDuration": 1800,
    "turnTimeout": 7,
    "silenceTimeout": 300,
    "turnMode": "silence"
  },
  "systemPrompt": "You are Bird Nova...",
  "firstMessage": "Hello there, fellow bird enthusiast!...",
  "confidence": 0.82,
  "category": "education",
  "description": "Bird Nova is an AI assistant..."
}`}
        value={testModeJson}
        onChange={(e) => setTestModeJson(e.target.value)}
        className="min-h-64 font-mono text-sm"
      />
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(testModeJson)}
          className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white"
        >
          Load Configuration
        </Button>
      </div>
    </div>
  );
};
